import { kpiHasRecordedValue } from './executive-pulse-kpi-value.js';

/**
 * Year-span chart data for Executive Pulse (cards + drill-down).
 * X-axis: reporting year start → end. Actuals through chartEndDate (usually today).
 * Dashed "pace" line: linear path from 0 at year start to target at year end.
 */

function parseDate(value) {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function dayStart(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function yearEndFromStart(periodStart) {
    const y = periodStart.getFullYear();
    return new Date(y, 11, 31, 23, 59, 59, 999);
}

export function formatChartAxisDate(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatChartTooltipDate(date) {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

/**
 * @param {object} item — KPI row
 * @param {object} [reportingPeriod] — `meta.reportingPeriod`
 */
export function resolveChartPeriod(item, reportingPeriod) {
    const periodStart = parseDate(
        reportingPeriod?.startDate || item?.startDate
    );
    let periodEnd = parseDate(reportingPeriod?.endDate);
    if (!periodEnd && periodStart) {
        periodEnd = yearEndFromStart(periodStart);
    }

    let dataEnd = parseDate(
        reportingPeriod?.chartEndDate ||
            item?.endDate ||
            reportingPeriod?.endDate
    );
    if (!dataEnd && periodEnd) {
        dataEnd = periodEnd;
    }
    if (periodEnd && dataEnd && dataEnd > periodEnd) {
        dataEnd = periodEnd;
    }

    const target = Number(item?.target) || 0;
    const today = dayStart(new Date());
    const showTodayMarker =
        periodStart &&
        periodEnd &&
        dataEnd &&
        today >= dayStart(periodStart) &&
        today <= dayStart(periodEnd) &&
        today.getTime() <= dayStart(dataEnd).getTime();

    return {
        periodStart,
        periodEnd,
        dataEnd,
        target,
        todayMs: showTodayMarker ? today.getTime() : null
    };
}

function parseSeriesPointDate(point) {
    if (point?.date) return parseDate(point.date);
    if (point?.label) {
        const parsed = Date.parse(point.label);
        if (!Number.isNaN(parsed)) return new Date(parsed);
    }
    return null;
}

function collectActualPoints(item, periodStart, dataEnd) {
    const startMs = dayStart(periodStart).getTime();
    const endMs = dayStart(dataEnd).getTime();
    const byDay = new Map();

    if (Array.isArray(item?.series)) {
        item.series.forEach((point) => {
            const d = parseSeriesPointDate(point);
            if (!d) return;
            const t = dayStart(d).getTime();
            if (t < startMs || t > endMs) return;
            byDay.set(t, Number(point.value));
        });
    }

    if (kpiHasRecordedValue(item)) {
        const value = Number(item.value);
        const endT = endMs;
        const existing = byDay.get(endT);
        if (existing == null || existing !== value) {
            byDay.set(endT, value);
        }
    }

    return [...byDay.entries()]
        .map(([t, v]) => ({ t, value: v }))
        .sort((a, b) => a.t - b.t);
}

function paceAtTime(t, periodStart, periodEnd, target) {
    const startMs = dayStart(periodStart).getTime();
    const endMs = dayStart(periodEnd).getTime();
    const span = endMs - startMs;
    if (span <= 0) return 0;
    const clamped = Math.min(Math.max(t, startMs), endMs);
    return (target * (clamped - startMs)) / span;
}

/**
 * @returns {{ data: object[], periodStart: Date|null, periodEnd: Date|null, dataEnd: Date|null, target: number, todayMs: number|null, hasActual: boolean }}
 */
export function buildQuarterChartData(item, reportingPeriod) {
    const period = resolveChartPeriod(item, reportingPeriod);
    const { periodStart, periodEnd, dataEnd, target, todayMs } = period;

    if (!periodStart || !periodEnd || !dataEnd) {
        return { ...period, data: [], hasActual: false };
    }

    const actualPoints = collectActualPoints(item, periodStart, dataEnd);
    const timeKeys = new Set([
        dayStart(periodStart).getTime(),
        dayStart(periodEnd).getTime(),
        dayStart(dataEnd).getTime()
    ]);
    actualPoints.forEach((p) => timeKeys.add(p.t));
    if (todayMs != null) timeKeys.add(todayMs);

    const actualByTime = new Map(actualPoints.map((p) => [p.t, p.value]));
    const dataEndMs = dayStart(dataEnd).getTime();

    // Progress charts assume zero at year open until the first logged entry.
    let lastActual = 0;
    const data = [...timeKeys]
        .sort((a, b) => a - b)
        .map((t) => {
            if (actualByTime.has(t)) {
                lastActual = actualByTime.get(t);
            }
            const date = new Date(t);
            const showActual = t <= dataEndMs ? lastActual : null;
            return {
                t,
                dateLabel: formatChartAxisDate(date),
                tooltipDate: formatChartTooltipDate(date),
                actual: showActual,
                pace: Number(
                    paceAtTime(t, periodStart, periodEnd, target).toFixed(2)
                ),
                isReading: actualByTime.has(t)
            };
        });

    return {
        ...period,
        data,
        hasActual: actualPoints.length > 0
    };
}

export function formatChartValue(item, value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(1);
}

export function chartSeriesCaption(item, reportingPeriod) {
    const { periodStart, periodEnd, dataEnd } = resolveChartPeriod(
        item,
        reportingPeriod
    );
    const range =
        periodStart && dataEnd
            ? `${formatChartAxisDate(periodStart)} – ${formatChartAxisDate(
                  dataEnd
              )} (actuals)`
            : '';
    const yearEndLabel =
        periodEnd && dataEnd && dayStart(periodEnd) > dayStart(dataEnd)
            ? ` Year ends ${formatChartAxisDate(periodEnd)}.`
            : '';

    let sourceNote = '';
    const liveSource = item?.seriesSource || item?.source;
    if (liveSource === 'hub') {
        sourceNote = 'Actuals from Hub.';
    } else if (liveSource === 'zoho') {
        sourceNote = 'Actuals from Zoho.';
    } else if (liveSource === 'manual' || kpiHasRecordedValue(item)) {
        sourceNote = 'Actuals from logged entries.';
    } else {
        sourceNote = 'No values logged yet — dashed line shows pace to goal.';
    }

    return `Solid line = progress through the year (starts at zero on day one). Dashed line = steady pace needed to hit the goal by year end. ${sourceNote}${
        range ? ` ${range}.` : ''
    }${yearEndLabel}`;
}

/** @deprecated Use buildQuarterChartData */
const buildChartData = (item, reportingPeriod) =>
    buildQuarterChartData(item, reportingPeriod).data;

export default buildChartData;
